import { Check, ChevronDown, Eye, Hand, Laptop, ShieldOff, Zap } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { MAC_PERMISSION_OPTIONS, type MacPermission } from '../lib/mac';

const ICONS: Record<MacPermission, ReactNode> = {
  read: <Eye size={14} />,
  ask: <Hand size={14} />,
  auto: <Zap size={14} />,
  bypass: <ShieldOff size={14} />,
};

type Props = {
  value: MacPermission;
  onChange: (p: MacPermission) => void;
  disabled?: boolean;
};

/** «Мак» chat access level. Applies to the next Mac tool call, even mid-run. */
export function MacPermissionPicker({ value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const current = MAC_PERMISSION_OPTIONS.find((m) => m.id === value) ?? MAC_PERMISSION_OPTIONS[1];

  const close = useCallback((): void => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  return (
    <div ref={wrapRef} className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Доступ к Маку: ${current?.label}`}
        className={cn(
          'mc-press inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-full px-2.5 text-[13px] hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-60',
          value === 'bypass' ? 'text-destructive' : 'text-foreground',
        )}
        title="Уровень доступа к Маку"
      >
        <Laptop size={14} className="shrink-0 text-primary" />
        <span className="shrink-0 text-muted-foreground">{ICONS[value]}</span>
        <span className="truncate">{current?.label}</span>
        <ChevronDown size={12} className="shrink-0" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Доступ к Маку"
          className="mc-menu-enter origin-bottom-left absolute bottom-[calc(100%+6px)] left-0 z-20 w-[min(calc(100vw-2rem),300px)] rounded-2xl border border-border/70 bg-surface p-1.5 mc-float"
        >
          {MAC_PERMISSION_OPTIONS.map((m) => {
            const active = m.id === value;
            return (
              <button
                key={m.id}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  onChange(m.id);
                  close();
                }}
                className={cn(
                  'flex min-h-11 w-full items-start gap-3 rounded-md px-3 py-2 text-left hover:bg-subtle',
                  active && 'bg-subtle',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 shrink-0 text-muted-foreground',
                    m.id === 'bypass' && 'text-destructive',
                  )}
                >
                  {ICONS[m.id]}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{m.label}</div>
                  <div className="text-[12px] text-muted-foreground">{m.sub}</div>
                </div>
                {active && <Check size={14} className="mt-1 shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
